// Embox's data layer: sign-in, connected accounts and live mail, exposed to the
// (framework-free) app as window.Embox. Queries are cached and deduplicated by
// TanStack Query's core (the engine behind react-query); the Inbox SDK's event
// stream invalidates them, so the list updates as mail arrives.
import { QueryClient, QueryObserver } from '@tanstack/query-core'
import { createAuthClient } from 'better-auth/client'
import { createInboxClient } from 'inbox-sdk/client'
import type { Connection, MailboxMessageSummary } from 'inbox-sdk/types'

export interface ServerInfo { signups: boolean; googleSignIn: boolean; writes: boolean; triage: boolean }
export interface User { id: string; email: string; name: string }
export type Session =
  | { mode: 'demo'; reason: 'no-server' | 'chosen' }
  | { mode: 'signed-out'; server: ServerInfo }
  | { mode: 'live'; user: User; server: ServerInfo }

export interface ProviderCard {
  id: string
  name: string
  brand: string
  kind: 'oauth' | 'password' | 'apiKey'
  summary: string
  ready: boolean
  unavailable?: string
  actionLabel: string
  redirectNote?: string
  fields?: { name: string; label: string; type: string; placeholder?: string; autocomplete?: string }[]
  help?: { text: string; url: string; linkLabel: string }
  alternative?: { card: string; label: string }
}

/** A message in the shape app.js renders (same fields as its demo mail), plus live identity. */
export interface Mail {
  id: string
  from: string
  fromEmail: string
  subject: string
  snippet: string
  at: Date
  unread: boolean
  archived: boolean
  sent: false
  label: string
  /** Sorted into Important by triage; undefined until (or unless) it has been classified. */
  important?: boolean
  body: string[]
  live: { mailboxId: string; revision: number; loaded: boolean }
}
export interface MailState {
  status: 'loading' | 'ready' | 'error'
  accounts: number
  messages: Mail[]
  /** Whether this server sorts mail into Important and Other, and how many unread messages it's still sorting. */
  triage: { enabled: boolean; pending: number }
  error?: string
}
interface TriageResponse { enabled: boolean; pending: number; items: { id: string; important: boolean; label: string }[] }

const DEMO_KEY = 'embox-demo'
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } },
})
queryClient.mount() // refetch on focus and reconnect

const auth = createAuthClient({ baseURL: location.origin, basePath: '/api/auth' })
const inbox = createInboxClient({ baseUrl: '/api/inbox', cacheScope: 'session' })
let server: ServerInfo | null = null

class ApiFailure extends Error {
  constructor(message: string, readonly code: string, readonly status: number) { super(message) }
}
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { 'Content-Type': 'application/json', ...init?.headers } })
  const body = await response.json().catch(() => null) as { error?: { code: string; message: string } } | null
  if (!response.ok) throw new ApiFailure(body?.error?.message ?? 'Something went wrong.', body?.error?.code ?? 'HTTP', response.status)
  return body as T
}

// ---- categories: the tube colour for a message, until a real classifier exists ----
function category(summary: MailboxMessageSummary): string {
  const text = `${summary.subject} ${summary.preview}`
  const facts = summary.facts
  if (/\b(verification|verify|sign[- ]?in|one[- ]time|passcode|security) (code|link)\b|\bcode is\b/i.test(text)) return 'Security'
  if (/\b(invoice|receipt|payment|statement|billing|refund)\b|[$€£]\s?\d/i.test(text)) return 'Finance'
  if (/\b(out for delivery|shipped|delivery|package|tracking)\b/i.test(text)) return 'Receipts'
  if (/\b(flight|check[- ]in|boarding|reservation|itinerary|booking)\b/i.test(text)) return 'Travel'
  if (facts?.listId || facts?.listUnsubscribe || facts?.bulk || facts?.unsubscribeLink) return 'Newsletter'
  if (facts?.automated) return 'Updates'
  return 'Personal'
}

function toMail(summary: MailboxMessageSummary): Mail {
  const membership = summary.memberships[0]!
  return {
    id: summary.id,
    from: summary.from.name || summary.from.email,
    fromEmail: summary.from.email,
    subject: summary.subject || '(no subject)',
    snippet: summary.preview,
    at: new Date(summary.receivedAt),
    unread: !summary.isRead,
    archived: membership.done,
    sent: false,
    label: category(summary),
    // The preview stands in for the body until the message is opened (beacons read it too).
    body: summary.preview ? [summary.preview] : [],
    live: { mailboxId: membership.mailboxId, revision: membership.revision, loaded: false },
  }
}

// Read state can only reach the provider when the server allows writes; otherwise it's kept here.
const readOverrides = new Map<string, boolean>()
const withOverrides = (mail: Mail[]) => mail.map(m => (readOverrides.has(m.id) ? { ...m, unread: !readOverrides.get(m.id) } : m))

interface MailData { accounts: number; messages: Mail[]; triage: MailState['triage'] }
const NO_TRIAGE = { enabled: false, pending: 0 }

async function loadMail(): Promise<MailData> {
  const mailboxes = (await inbox.mailboxes()).filter(mailbox => mailbox.status === 'active')
  if (!mailboxes.length) return { accounts: 0, messages: [], triage: NO_TRIAGE }
  const mailboxIds = mailboxes.slice(0, 50).map(mailbox => mailbox.id)
  const [open, done, triage] = await Promise.all([
    inbox.mailboxMessages({ mailboxIds, done: false, limit: 100 }),
    inbox.mailboxMessages({ mailboxIds, done: true, limit: 50 }),
    server?.triage ? api<TriageResponse>('/api/triage').catch(() => null) : null,
  ])
  const decisions = new Map(triage?.items.map(item => [item.id, item]))
  const seen = new Set<string>()
  const messages = [...open.items, ...done.items]
    .filter(item => item.memberships.length && !seen.has(item.id) && seen.add(item.id))
    .map(summary => {
      const mail = toMail(summary)
      const decision = decisions.get(mail.id)
      return decision ? { ...mail, important: decision.important, label: decision.label } : mail
    })
  return {
    accounts: new Set(mailboxes.map(mailbox => mailbox.connectionId)).size,
    messages,
    triage: triage ? { enabled: triage.enabled, pending: triage.pending } : NO_TRIAGE,
  }
}

const MAIL_KEY = ['mail'] as const
let streamStop: (() => void) | null = null
let invalidateTimer = 0
function startStream() {
  if (streamStop) return
  // Events carry IDs, not mail; bursts during an import collapse into one refetch.
  streamStop = inbox.subscribe(() => {
    clearTimeout(invalidateTimer)
    invalidateTimer = window.setTimeout(() => { void queryClient.invalidateQueries({ queryKey: MAIL_KEY }) }, 300)
  }, { onError: () => {} })
}

const Embox = {
  queryClient,

  async init(): Promise<Session> {
    if (sessionStorage.getItem(DEMO_KEY)) return { mode: 'demo', reason: 'chosen' }
    let body: { user: User | null; server: ServerInfo }
    try {
      const response = await fetch('/api/session', { headers: { Accept: 'application/json' } })
      if (!response.ok) return { mode: 'demo', reason: 'no-server' }
      body = await response.json()
    } catch {
      return { mode: 'demo', reason: 'no-server' }
    }
    server = body.server
    return body.user ? { mode: 'live', user: body.user, server: body.server } : { mode: 'signed-out', server: body.server }
  },

  demo: {
    start() { sessionStorage.setItem(DEMO_KEY, '1') },
    leave() { sessionStorage.removeItem(DEMO_KEY) },
  },

  auth: {
    async signIn(email: string, password: string) {
      const { error } = await auth.signIn.email({ email, password })
      if (error) throw new ApiFailure(error.message || 'That email and password don’t match.', error.code ?? 'AUTH', error.status ?? 401)
    },
    async signUp(name: string, email: string, password: string) {
      const { error } = await auth.signUp.email({ name, email, password })
      if (error) throw new ApiFailure(error.message || 'Couldn’t create that account.', error.code ?? 'AUTH', error.status ?? 400)
    },
    async signInWithGoogle() {
      const { error } = await auth.signIn.social({ provider: 'google', callbackURL: '/' })
      if (error) throw new ApiFailure(error.message || 'Google sign-in failed.', error.code ?? 'AUTH', error.status ?? 400)
    },
    async signOut() {
      streamStop?.(); streamStop = null
      queryClient.clear()
      await auth.signOut()
    },
  },

  providers: {
    list: () => queryClient.fetchQuery({
      queryKey: ['providers'],
      queryFn: () => api<{ cards: ProviderCard[]; alternatives: ProviderCard[]; connections: Connection[] }>('/api/providers'),
      staleTime: 5_000,
    }),
    async connect(card: string, fields: Record<string, string> = {}) {
      const result = await api<{ connectionId?: string; authorizeUrl?: string }>('/api/connect', { method: 'POST', body: JSON.stringify({ card, fields }) })
      if (result.connectionId) {
        void queryClient.invalidateQueries({ queryKey: ['providers'] })
        void queryClient.invalidateQueries({ queryKey: MAIL_KEY })
      }
      return result
    },
    async disconnect(connectionId: string) {
      await inbox.disconnectConnection(connectionId)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['providers'] }),
        queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
      ])
    },
  },

  mail: {
    /** Live mail as Embox rows. The listener runs now and on every change. */
    subscribe(listener: (state: MailState) => void): () => void {
      startStream()
      const observer = new QueryObserver(queryClient, {
        queryKey: MAIL_KEY,
        queryFn: loadMail,
        // Look again soon while a new account is importing, or while unread mail is still being sorted.
        refetchInterval: query => {
          const data = query.state.data
          if (!data?.accounts) return false
          return !data.messages.length ? 3_000 : data.triage.pending > 0 ? 2_500 : false
        },
      })
      const emit = () => {
        const result = observer.getCurrentResult()
        listener({
          status: result.data ? 'ready' : result.isError ? 'error' : 'loading',
          accounts: result.data?.accounts ?? 0,
          messages: withOverrides(result.data?.messages ?? []),
          triage: result.data?.triage ?? NO_TRIAGE,
          ...(result.error ? { error: result.error.message } : {}),
        })
      }
      const unsubscribe = observer.subscribe(emit)
      emit()
      return unsubscribe
    },

    /** Full text for an open message, cached. */
    async open(mail: Mail): Promise<string[]> {
      const message = await queryClient.fetchQuery({
        queryKey: ['message', mail.id],
        queryFn: () => inbox.mailboxMessage(mail.live.mailboxId, mail.id),
        staleTime: Infinity,
      })
      const text = (message.bodyText || '').replace(/\r\n/g, '\n').trim()
      return text ? text.split(/\n{2,}/).map(paragraph => paragraph.trim()).filter(Boolean) : [message.preview || '(This message has no text.)']
    },

    /** Archive (or bring back) in Embox: mailbox-local "done", which works even when provider writes are off. */
    async setArchived(mail: Mail, archived: boolean) {
      const membership = await inbox.setMailboxState(mail.live.mailboxId, mail.id, { done: archived }, mail.live.revision)
      mail.live.revision = membership.revision
      queryClient.setQueryData<MailData>(MAIL_KEY, data => data && {
        ...data,
        messages: data.messages.map(m => (m.id === mail.id ? { ...m, archived, live: { ...m.live, revision: membership.revision } } : m)),
      })
    },

    async setRead(mail: Mail, read: boolean) {
      readOverrides.set(mail.id, read)
      if (!server?.writes) return
      await inbox.mutate({ messageIds: [mail.id], changes: { isRead: read }, idempotencyKey: crypto.randomUUID(), viaMailboxId: mail.live.mailboxId })
      readOverrides.delete(mail.id)
      void queryClient.invalidateQueries({ queryKey: MAIL_KEY })
    },

    refresh: () => queryClient.invalidateQueries({ queryKey: MAIL_KEY }),
  },
}

declare global { interface Window { Embox: typeof Embox } }
window.Embox = Embox
export type EmboxApi = typeof Embox
